/**
 * Figure S6's "Portfolio entities" card — the whole left column of the
 * Portfolio Overview: the ordering and filter row, the table, the legend and
 * the pager. One row per entity; clicking a row zooms in to that Entity.
 *
 * The Sharing column is the point of the card. It says what this inventor has
 * shared with this portfolio and how they came to be in it — never what
 * anyone pays for. ★ means "by invitation"; the legend under the table says
 * what that does and does not buy the portfolio.
 */
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { entityGroupPath, formatLastActivity } from './entityGroups'
import {
  NO_STAGE,
  NO_STAGE_FOOTNOTE,
  NO_STAGE_REASON,
  ROW_ORDERS,
  ROW_ORDER_LABEL,
  entitySubLine,
  filterRows,
  ipPostureOptions,
  orderRows,
  splitLastWord,
  stageOptions,
} from './portfolioRows'
import type { PortfolioEntityRow, RowOrder } from './portfolioRows'
import { BY_INVITATION_LEGEND, byInvitationTitle, sharingLines } from './sharing'

export const PAGE_SIZE = 7

export interface PortfolioEntitiesProps {
  rows: PortfolioEntityRow[]
  portfolioName: string
  order: RowOrder
  onOrderChange: (order: RowOrder) => void
  intakeFilter: string | null
  onIntakeFilterChange: (intake: string | null) => void
  stageFilter: string | null
  onStageFilterChange: (stage: string | null) => void
  /** The figure's third filter: Inventor retains / Option / Licence. */
  ipFilter: string | null
  onIpFilterChange: (posture: string | null) => void
  page: number
  onPageChange: (page: number) => void
}

/**
 * An active filter, as the figure draws it: "Intake 2 ✕". Clicking it clears
 * that one filter. Round 2 had this only for intake, and only because the
 * intake was set from a row; the stage and IP filters left no trace on the
 * page at all once chosen.
 */
function FilterChip({ label, onClear, testId }: { label: string; onClear: () => void; testId: string }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClear}
      className="inline-flex items-center gap-1 rounded-full bg-iflow-100 px-2.5 py-1 text-[12px] font-semibold text-iflow-700"
      title={`Showing only ${label} · click to clear`}
    >
      {label} <X size={12} aria-hidden />
      <span className="sr-only">clear this filter</span>
    </button>
  )
}

const SELECT_CLASS =
  'rounded-lg border border-iflow-900/15 bg-white px-2.5 py-1.5 text-[12px] text-iflow-900'

export default function PortfolioEntities({
  rows,
  portfolioName,
  order,
  onOrderChange,
  intakeFilter,
  onIntakeFilterChange,
  stageFilter,
  onStageFilterChange,
  ipFilter,
  onIpFilterChange,
  page,
  onPageChange,
}: PortfolioEntitiesProps) {
  const visible = orderRows(
    filterRows(rows, { intake: intakeFilter, stage: stageFilter, ip: ipFilter }),
    order,
  )
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const current = Math.min(page, pageCount - 1)
  const shown = visible.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE)
  const stages = stageOptions(rows)
  const postures = ipPostureOptions(rows)
  const anyStageMissing = rows.some((row) => !row.stage)

  return (
    <div data-part="PortfolioEntities" className="light-card p-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h3 className="text-[15px] font-bold text-iflow-900">Portfolio entities</h3>
        <span className="text-[12px] text-gray-500">
          · grouped by entity group · click a row to zoom in
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="portfolio-entities-order">Order the rows</label>
        <select
          id="portfolio-entities-order"
          className={SELECT_CLASS}
          title="Order the rows"
          value={order}
          onChange={(e) => onOrderChange(e.target.value as RowOrder)}
        >
          {ROW_ORDERS.map((value) => (
            <option key={value} value={value}>{ROW_ORDER_LABEL[value]}</option>
          ))}
        </select>

        {/* The figure puts the active-filter chip SECOND, immediately after the
            ordering select — where the eye lands after reading how the table
            is sorted — not at the end of the control row. */}
        {intakeFilter && (
          <FilterChip
            testId="intake-filter-pill"
            label={intakeFilter}
            onClear={() => onIntakeFilterChange(null)}
          />
        )}
        {stageFilter && (
          <FilterChip
            testId="stage-filter-pill"
            label={stageFilter}
            onClear={() => onStageFilterChange(null)}
          />
        )}
        {ipFilter && (
          <FilterChip
            testId="ip-filter-pill"
            label={ipFilter}
            onClear={() => onIpFilterChange(null)}
          />
        )}

        <label className="sr-only" htmlFor="portfolio-entities-stage">Filter by stage</label>
        <select
          id="portfolio-entities-stage"
          className={SELECT_CLASS}
          title="The stage this portfolio has recorded against the entity."
          value={stageFilter ?? ''}
          onChange={(e) => onStageFilterChange(e.target.value || null)}
        >
          <option value="">All stages</option>
          {stages.map((stage) => (
            <option key={stage} value={stage}>{stage}</option>
          ))}
        </select>

        <label className="sr-only" htmlFor="portfolio-entities-ip">Filter by IP posture</label>
        <select
          id="portfolio-entities-ip"
          className={SELECT_CLASS}
          title="What is on record about the IP: the inventor retains it unless an option, licence or assignment has been recorded. A record never changes what you can see."
          value={ipFilter ?? ''}
          onChange={(e) => onIpFilterChange(e.target.value || null)}
        >
          <option value="">IP posture: any</option>
          {postures.map((posture) => (
            <option key={posture} value={posture}>{posture}</option>
          ))}
        </select>

      </div>

      {rows.length === 0 ? (
        <p className="mt-4 text-[13px] text-gray-600">
          No entities in this portfolio yet. An inventor joins by accepting your invitation, or by
          sharing a project with you directly.
        </p>
      ) : visible.length === 0 ? (
        <p className="mt-4 text-[13px] text-gray-600">
          No entity matches these filters.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="bg-iflow-900 text-white">
                <th className="rounded-l-lg px-3 py-2 font-semibold">Entity</th>
                <th className="px-3 py-2 font-semibold">Stage</th>
                <th className="px-3 py-2 font-semibold">Projects</th>
                <th className="px-3 py-2 font-semibold">Sharing</th>
                <th className="rounded-r-lg px-3 py-2 font-semibold">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const lines = sharingLines(row.sharing)
                return (
                  <tr
                    key={row.key}
                    data-testid="portfolio-entity-row"
                    className="group border-b border-iflow-900/5 hover:bg-iflow-50"
                  >
                    {/* The name wraps anywhere a name may wrap EXCEPT before
                        its last word: that word and the ★ share one nowrap
                        span, so the mark can never be orphaned and the name is
                        never truncated. See splitLastWord for why the no-break
                        space that round 6 shipped was not enough. */}
                    <td className="px-3 py-2 align-middle">
                      <Link
                        to={entityGroupPath(row.label)}
                        title={row.label}
                        className="font-bold text-iflow-900 hover:underline"
                      >
                        {splitLastWord(row.label).head
                          ? `${splitLastWord(row.label).head} ` : ''}
                        <span className="whitespace-nowrap">
                          {splitLastWord(row.label).last}
                          {row.sharing.byInvitation && (
                            <span
                              className="ml-1.5 inline-block rounded-full bg-iflow-100 px-1.5 py-0.5 text-[11px] font-semibold text-iflow-700"
                              title={byInvitationTitle(portfolioName)}
                              data-testid="by-invitation-star"
                            >
                              ★
                            </span>
                          )}
                        </span>
                      </Link>
                      {/* The group this row is filed under, and the IP posture
                          when there is an arrangement — never the intake, which
                          under an intake filter would repeat the filter's own
                          value once per row for no information. */}
                      {entitySubLine(row) && (
                        <div
                          data-testid="entity-sub-line"
                          className="truncate text-[11.5px] text-gray-500"
                          title={entitySubLine(row) ?? undefined}
                        >
                          {entitySubLine(row)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      {row.stage ? (
                        <span className="inline-block rounded-full bg-iflow-100 px-2 py-0.5 text-[11.5px] font-semibold text-iflow-700">
                          {row.stage}
                        </span>
                      ) : (
                        <span className="text-gray-400" title={NO_STAGE_REASON}>{NO_STAGE}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle text-iflow-900">{row.projectCount}</td>
                    <td className="px-3 py-2 align-middle">
                      <div className="text-iflow-900">{lines.primary}</div>
                      {lines.secondary && <div className="text-[11.5px] text-gray-500">{lines.secondary}</div>}
                    </td>
                    <td className="px-3 py-2 align-middle text-gray-500">
                      <span className="group-hover:hidden">
                        {formatLastActivity(row.lastActivity) ?? 'No activity'}
                      </span>
                      <Link
                        to={entityGroupPath(row.label)}
                        className="hidden font-semibold text-iflow-700 hover:text-iflow-800 group-hover:inline"
                        title="Zoom in to Entity"
                      >
                        Open {row.label} →
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-start justify-between gap-4">
        <p className="min-w-0 flex-1 text-[12px] text-gray-500">
          {visible.length > 0 && (
            <>
              Showing {shown.length} of {visible.length}
              {' · '}
            </>
          )}
          {BY_INVITATION_LEGEND}
          {anyStageMissing && (
            <>
              {' · '}
              <span data-testid="no-stage-footnote">{NO_STAGE_FOOTNOTE}</span>
            </>
          )}
        </p>
        {pageCount > 1 && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => onPageChange(current - 1)}
              disabled={current === 0}
              className="inline-flex items-center rounded-lg border border-iflow-900/15 bg-white px-2 py-1 text-[12px] text-iflow-800 disabled:opacity-40"
            >
              <ChevronLeft size={12} aria-hidden /> Prev
            </button>
            <span className="text-[12px] text-gray-500">{current + 1} of {pageCount}</span>
            <button
              type="button"
              onClick={() => onPageChange(current + 1)}
              disabled={current >= pageCount - 1}
              className="inline-flex items-center rounded-lg border border-iflow-900/15 bg-white px-2 py-1 text-[12px] text-iflow-800 disabled:opacity-40"
            >
              Next <ChevronRight size={12} aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
